import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, MessageCircle, Calendar, Eye, ThumbsUp, 
  Clock, Globe, Activity, BarChart3, PieChart, LineChart,
  Download, Filter, RefreshCw, Search, ArrowUp, ArrowDown,
  UserCheck, UserPlus, Heart, Share2, Target, Award,
  Star, CheckCircle, Pin
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const CommunityStats = () => {
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('overview');

  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchCommunityStats();
  }, [timeRange]);

  const fetchCommunityStats = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/admin/community-stats`, {
        params: { timeRange }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch community stats:', error);
      // Set empty data structure on error
      setStats({
        overview: {
          totalUsers: 0,
          activeUsers: 0,
          newUsers: 0,
          totalPosts: 0,
          totalComments: 0,
          totalLikes: 0,
          engagementRate: 0,
          retentionRate: 0
        },
        growth: {
          userGrowth: 0,
          postGrowth: 0,
          engagementGrowth: 0,
          revenueGrowth: 0
        },
        engagement: {
          dailyActiveUsers: [],
          postsPerDay: [],
          commentsPerPost: 0,
          likesPerPost: 0,
          shareRate: 0,
          timeSpentAvg: '0m 0s'
        },
        demographics: {
          topCountries: [],
          ageGroups: [],
          industries: []
        },
        content: {
          topPosts: [],
          categories: [],
          moderationQueue: {
            pending: 0,
            reported: 0,
            flagged: 0
          }
        },
        performance: {
          pageLoadTime: 'N/A',
          serverUptime: 'N/A',
          errorRate: 'N/A',
          cacheHitRate: 'N/A'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-turquoise-400"></div>
          <span className="text-gray-400">Loading community analytics...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Community Analytics Dashboard</h2>
          <p className="text-gray-400">Comprehensive insights and performance metrics</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          
          <Button onClick={fetchCommunityStats} className="btn-secondary">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          
          <Button className="btn-primary-enhanced">
            <Download size={16} className="mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex space-x-1 bg-gray-800 rounded-lg p-1">
        {[
          { id: 'overview', label: '📊 Overview', icon: BarChart3 },
          { id: 'users', label: '👥 Users', icon: Users },
          { id: 'engagement', label: '💬 Engagement', icon: MessageCircle },
          { id: 'content', label: '📝 Content', icon: Eye },
          { id: 'performance', label: '⚡ Performance', icon: Activity }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSelectedMetric(tab.id)}
            className={`flex items-center px-4 py-2 rounded-md transition-colors ${
              selectedMetric === tab.id
                ? 'bg-turquoise-500 text-white'
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <tab.icon size={16} className="mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Metrics */}
      {selectedMetric === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Key Metrics */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Total Users</h3>
              <Users className="text-blue-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.overview.totalUsers.toLocaleString()}</div>
            <div className="flex items-center text-green-400 text-sm">
              <ArrowUp size={16} className="mr-1" />
              +{stats.growth.userGrowth}% from last period
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Active Users</h3>
              <Activity className="text-green-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.overview.activeUsers.toLocaleString()}</div>
            <div className="text-gray-400 text-sm">
              {((stats.overview.activeUsers / stats.overview.totalUsers) * 100).toFixed(1)}% of total users
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Total Posts</h3>
              <MessageCircle className="text-purple-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.overview.totalPosts.toLocaleString()}</div>
            <div className="flex items-center text-green-400 text-sm">
              <ArrowUp size={16} className="mr-1" />
              +{stats.growth.postGrowth}% from last period
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Engagement Rate</h3>
              <TrendingUp className="text-purple-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.overview.engagementRate.toFixed(1)}%</div>
            <div className="text-gray-400 text-sm">Average engagement per post</div>
          </div>

          {/* Best Answers Section */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Best Answers</h3>
              <Star className="text-yellow-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.best_answers || 0}</div>
            <div className="text-gray-400 text-sm">Replies marked as solutions</div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Solved Posts</h3>
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.solved_posts || 0}</div>
            <div className="text-gray-400 text-sm">Posts with marked solutions</div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Pinned Posts</h3>
              <Pin className="text-blue-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.pinned_posts || 0}</div>
            <div className="text-gray-400 text-sm">Important highlighted posts</div>
          </div>
        </div>
      )}

      {/* User Analytics */}
      {selectedMetric === 'users' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Demographics */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Geographic Distribution</h3>
            <div className="space-y-3">
              {stats.demographics.topCountries.length > 0 ? (
                stats.demographics.topCountries.map((country, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-300">{country.name}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-turquoise-400 h-2 rounded-full"
                          style={{ width: `${country.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-400 w-16 text-right">
                        {country.users} ({country.percentage}%)
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-sm">No geographic data available yet</div>
                  <div className="text-xs text-gray-500 mt-2">Data will appear as users join the community</div>
                </div>
              )}
            </div>
          </div>

          {/* Age Groups */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Age Distribution</h3>
            <div className="space-y-3">
              {stats.demographics.ageGroups.length > 0 ? (
                stats.demographics.ageGroups.map((age, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-gray-300">{age.range} years</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-blue-400 h-2 rounded-full"
                          style={{ width: `${age.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-400 w-12 text-right">{age.percentage}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-sm">No age demographic data available</div>
                  <div className="text-xs text-gray-500 mt-2">Age distribution will show as user profiles are completed</div>
                </div>
              )}
            </div>
          </div>

          {/* Industry Breakdown */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 lg:col-span-2">
            <h3 className="text-lg font-bold text-white mb-4">Industry Representation</h3>
            {stats.demographics.industries.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {stats.demographics.industries.map((industry, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-turquoise-400 mb-1">{industry.percentage}%</div>
                    <div className="text-sm text-gray-400">{industry.name}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-sm">No industry data available</div>
                <div className="text-xs text-gray-500 mt-2">Industry representation will appear as users complete their profiles</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Engagement Analytics */}
      {selectedMetric === 'engagement' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Engagement Metrics */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Engagement Overview</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400 mb-1">{stats.engagement.commentsPerPost}</div>
                <div className="text-sm text-gray-400">Comments per Post</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400 mb-1">{stats.engagement.likesPerPost}</div>
                <div className="text-sm text-gray-400">Likes per Post</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400 mb-1">{stats.engagement.shareRate}%</div>
                <div className="text-sm text-gray-400">Share Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400 mb-1">{stats.engagement.timeSpentAvg}</div>
                <div className="text-sm text-gray-400">Avg. Time Spent</div>
              </div>
            </div>
          </div>

          {/* Daily Activity Chart */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Daily Active Users (Last 7 Days)</h3>
            {stats.engagement.dailyActiveUsers.length > 0 ? (
              <div className="h-40 flex items-end justify-between space-x-2">
                {stats.engagement.dailyActiveUsers.map((users, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <div
                      className="bg-turquoise-400 rounded-t-md"
                      style={{
                        height: `${(users / Math.max(...stats.engagement.dailyActiveUsers)) * 120}px`,
                        width: '32px'
                      }}
                    ></div>
                    <div className="text-xs text-gray-400 mt-2">{users}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-gray-400 text-sm">No activity data available yet</div>
                  <div className="text-xs text-gray-500 mt-2">Daily activity tracking is being implemented</div>
                </div>
              </div>
            )}
          </div>

          {/* Retention Metrics */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 lg:col-span-2">
            <h3 className="text-lg font-bold text-white mb-4">Retention & Growth Metrics</h3>
            <div className="grid grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-400 mb-2">{stats.overview.retentionRate}%</div>
                <div className="text-sm text-gray-400">User Retention</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-400 mb-2">{stats.overview.newUsers}</div>
                <div className="text-sm text-gray-400">New Users</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-400 mb-2">{stats.overview.totalComments.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Total Comments</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-400 mb-2">{stats.overview.totalLikes.toLocaleString()}</div>
                <div className="text-sm text-gray-400">Total Likes</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Analytics */}
      {selectedMetric === 'content' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Performing Posts */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Top Performing Posts</h3>
            <div className="space-y-4">
              {stats.content.topPosts.map((post, index) => (
                <div key={post.id} className="p-3 bg-gray-800 rounded-lg">
                  <div className="font-medium text-white mb-2 truncate">{post.title}</div>
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <span className="flex items-center">
                      <Eye size={14} className="mr-1" />
                      {post.views} views
                    </span>
                    <span className="flex items-center">
                      <Heart size={14} className="mr-1" />
                      {post.likes} likes
                    </span>
                    <span className="flex items-center">
                      <MessageCircle size={14} className="mr-1" />
                      {post.comments} comments
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category Performance */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-4">Category Performance</h3>
            {stats.content.categories.length > 0 ? (
              <div className="space-y-4">
                {stats.content.categories.map((category, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                    <div>
                      <div className="font-medium text-white">{category.name}</div>
                      <div className="text-sm text-gray-400">{category.posts} posts</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-turquoise-400">{category.engagement}%</div>
                      <div className="text-xs text-gray-400">engagement</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 text-sm">No category data available</div>
                <div className="text-xs text-gray-500 mt-2">Category performance will show when posts are categorized</div>
              </div>
            )}
          </div>

          {/* Moderation Queue */}
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700 lg:col-span-2">
            <h3 className="text-lg font-bold text-white mb-4">Moderation Dashboard</h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <div className="text-2xl font-bold text-yellow-400 mb-1">{stats.content.moderationQueue.pending}</div>
                <div className="text-sm text-yellow-300">Pending Review</div>
              </div>
              <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <div className="text-2xl font-bold text-red-400 mb-1">{stats.content.moderationQueue.reported}</div>
                <div className="text-sm text-red-300">Reported Content</div>
              </div>
              <div className="text-center p-4 bg-orange-500/10 rounded-lg border border-orange-500/20">
                <div className="text-2xl font-bold text-orange-400 mb-1">{stats.content.moderationQueue.flagged}</div>
                <div className="text-sm text-orange-300">Flagged Posts</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {selectedMetric === 'performance' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Page Load Time</h3>
              <Clock className="text-green-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.performance.pageLoadTime}</div>
            <div className="text-green-400 text-sm">Excellent performance</div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Server Uptime</h3>
              <Activity className="text-blue-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.performance.serverUptime}</div>
            <div className="text-blue-400 text-sm">System stable</div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Error Rate</h3>
              <Target className="text-purple-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.performance.errorRate}</div>
            <div className="text-purple-400 text-sm">Very low errors</div>
          </div>

          <div className="bg-gray-900 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Cache Hit Rate</h3>
              <Award className="text-orange-400" size={24} />
            </div>
            <div className="text-3xl font-bold text-white mb-2">{stats.performance.cacheHitRate}</div>
            <div className="text-orange-400 text-sm">Optimized caching</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityStats;